import { useMemo } from 'react';
import type {
  RoastExtraColumnKey,
  RoastProfileRow,
  RoastStageState,
  RoastChartPoint,
  RoastPhaseArea,
  RoastStageMarker,
} from './types';
import { parseTime } from './RoastProfileUtils';
import { useLanguage } from '../../../features/language/useLanguage';

interface UseRoastChartDataArgs {
  rows: RoastProfileRow[];
  stages?: RoastStageState[];
  extraColumns: RoastExtraColumnKey[];
}

interface RoastChartLegendItem {
  key: string;
  label: string;
  color: string;
}

interface UseRoastChartDataResult {
  chartData: RoastChartPoint[];
  xTicks: number[];
  legendItems: RoastChartLegendItem[];
  tempAxisMax: number;
  tempTicks: number[];
  rightAxisMax: number;
  rightTicks: number[];
  phaseAreas: RoastPhaseArea[];
  stageMarkers: RoastStageMarker[];
  hasAirTempData: boolean;
  hasDrumTempData: boolean;
  hasEnvironmentTempData: boolean;
  hasDrumRpmData: boolean;
}

/**
 * Derives every value the roast chart needs from the table rows, stage states and
 * selected extra columns. Pure derivation only — kept separate from RoastProfileSection's
 * stopwatch/notification effects so the live-timer flow stays untouched.
 */
export function useRoastChartData({ rows, stages, extraColumns }: UseRoastChartDataArgs): UseRoastChartDataResult {
  const { t } = useLanguage();

  const chartData = useMemo(() => {
    const result: RoastChartPoint[] = [];
    let lastFire: number | null = null;
    let lastAirflow: number | null = null;
    let lastAirTemp: number | null = null;
    let lastDrumTemp: number | null = null;
    let lastEnvTemp: number | null = null;
    let lastDrumRpm: number | null = null;

    // Combine rows with stage occurrences to ensure the chart line reaches stage times
    type DataPoint = {
      seconds: number;
      row?: RoastProfileRow;
      stage?: RoastStageState;
    };

    const combinedPoints: DataPoint[] = rows.map(r => ({ seconds: r.seconds, row: r }));

    if (stages) {
      stages.forEach(s => {
        const t = parseTime(s.time);
        if (t !== null && s.temp !== '') {
          const sec = Math.round(t * 60);
          // Check if we already have a point at this exact second
          const existing = combinedPoints.find(p => p.seconds === sec);
          if (existing) {
            existing.stage = s;
          } else {
            combinedPoints.push({ seconds: sec, stage: s });
          }
        }
      });
    }

    // Sort by time
    combinedPoints.sort((a, b) => a.seconds - b.seconds);

    // Find the last index with valid bean temp among rows
    let lastBeanIndex = -1;
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].bean !== null && rows[i].bean !== undefined) {
        lastBeanIndex = rows[i].seconds;
        break;
      }
    }

    // Also consider stages if they have temp
    if (stages) {
      stages.forEach(s => {
        if (s.temp !== '') {
          const t = parseTime(s.time);
          if (t !== null) {
            const sec = Math.round(t * 60);
            if (sec > lastBeanIndex) lastBeanIndex = sec;
          }
        }
      });
    }

    for (let i = 0; i < combinedPoints.length; i++) {
      const p = combinedPoints[i];
      const r = p.row;
      const s = p.stage;

      // Update current values if present in the row
      if (r) {
        if (r.fire !== null && r.fire !== undefined) lastFire = r.fire;
        if (r.airflow !== null && r.airflow !== undefined) lastAirflow = r.airflow;
        if (r.airTemp !== null && r.airTemp !== undefined) lastAirTemp = r.airTemp;
        if (r.drumTemp !== null && r.drumTemp !== undefined) lastDrumTemp = r.drumTemp;
        if (r.environmentTemp !== null && r.environmentTemp !== undefined) lastEnvTemp = r.environmentTemp;
        if (r.drumRpm !== null && r.drumRpm !== undefined) lastDrumRpm = r.drumRpm;
      }

      // Only apply forward fill if within the range of data
      const isWithinRange = lastBeanIndex === -1 || p.seconds <= lastBeanIndex;

      result.push({
        timeStr: r?.time || s?.time || '',
        time: p.seconds / 60,
        bean: r?.bean ?? (s?.temp !== '' ? Number(s?.temp) : null),
        ror: r?.ror ?? null,
        fire: isWithinRange ? lastFire : null,
        airflow: isWithinRange ? lastAirflow : null,
        airTemp: isWithinRange ? (r?.airTemp ?? lastAirTemp) : null,
        drumTemp: isWithinRange ? (r?.drumTemp ?? lastDrumTemp) : null,
        environmentTemp: isWithinRange ? (r?.environmentTemp ?? lastEnvTemp) : null,
        drumRpm: isWithinRange ? (r?.drumRpm ?? lastDrumRpm) : null,
      });
    }

    return result;
  }, [rows, stages]);

  // Generate X-axis ticks every 30 seconds (0.5 minutes)
  const xTicks = useMemo(() => {
    let maxTime = 0;

    if (chartData.length > 0) {
      maxTime = Math.ceil(chartData[chartData.length - 1].time);
    }

    // Also consider stage times to extend axis if needed
    if (stages) {
      stages.forEach(s => {
        const t = parseTime(s.time);
        if (t !== null && t > maxTime) {
          maxTime = Math.ceil(t);
        }
      });
    }

    // Default minimum time if empty
    if (maxTime === 0 && chartData.length === 0) maxTime = 10;

    const ticks = [];
    for (let t = 0; t <= maxTime + 1; t += 0.5) {
      ticks.push(t);
    }
    return ticks;
  }, [chartData, stages]);

  const hasAirTempData = extraColumns.includes('airTemp') && rows.some((r) => r.airTemp != null);
  const hasDrumTempData = extraColumns.includes('drumTemp') && rows.some((r) => r.drumTemp != null);
  const hasEnvironmentTempData = extraColumns.includes('environmentTemp') && rows.some((r) => r.environmentTemp != null);
  const hasDrumRpmData = extraColumns.includes('drumRpm') && rows.some((r) => r.drumRpm != null);

  const legendItems = useMemo(() => {
    const items: RoastChartLegendItem[] = [
      { key: 'bean', label: t('roast.profile.legend.beanTemp'), color: '#16a34a' },
      { key: 'ror', label: t('roast.profile.legend.ror'), color: '#eab308' },
      { key: 'fire', label: t('roast.profile.legend.fire'), color: '#dc2626' },
      { key: 'airflow', label: t('roast.profile.legend.airflow'), color: '#2563eb' },
    ];

    if (hasAirTempData) items.push({ key: 'airTemp', label: t('roast.profile.legend.airTemp'), color: '#0ea5e9' });
    if (hasDrumTempData) items.push({ key: 'drumTemp', label: t('roast.profile.legend.drumTemp'), color: '#78350f' });
    if (hasEnvironmentTempData) items.push({ key: 'environmentTemp', label: t('roast.profile.legend.environmentTemp'), color: '#a855f7' });
    if (hasDrumRpmData) items.push({ key: 'drumRpm', label: t('roast.profile.legend.drumRpm'), color: '#f97316' });

    return items;
  }, [hasAirTempData, hasDrumTempData, hasEnvironmentTempData, hasDrumRpmData, t]);

  const maxTemp = useMemo(() => {
    let max = 0;
    rows.forEach((r) => {
      if (r.bean && r.bean > max) max = r.bean;
      if (extraColumns.includes('airTemp') && r.airTemp && r.airTemp > max) max = r.airTemp;
      if (extraColumns.includes('drumTemp') && r.drumTemp && r.drumTemp > max) max = r.drumTemp;
      if (extraColumns.includes('environmentTemp') && r.environmentTemp && r.environmentTemp > max) max = r.environmentTemp;
      if (r.fire && r.fire > max) max = r.fire;
      if (r.airflow && r.airflow > max) max = r.airflow;
    });
    // Default max if no data
    return max > 0 ? max : 200;
  }, [rows, extraColumns]);

  const tempAxisMax = useMemo(() => Math.ceil(maxTemp) + 30, [maxTemp]);

  const tempTicks = useMemo(() => {
    const ticks = new Set<number>([-5]);
    if (tempAxisMax > 0) {
      const stepRaw = tempAxisMax / 4;
      const roundToNice = (value: number) => {
        const rounded = Math.round(value / 5) * 5;
        return rounded > 0 ? rounded : Math.ceil(value);
      };

      for (let i = 1; i <= 3; i += 1) {
        const tick = roundToNice(stepRaw * i);
        if (tick > 0 && tick < tempAxisMax) {
          ticks.add(tick);
        }
      }
      ticks.add(tempAxisMax);
    }
    return Array.from(ticks).sort((a, b) => a - b);
  }, [tempAxisMax]);

  const maxRightAxisValue = useMemo(() => {
    let max = 0;
    rows.forEach(r => {
      if (typeof r.ror === 'number' && r.ror > max) max = r.ror;
      if (extraColumns.includes('drumRpm') && typeof r.drumRpm === 'number' && r.drumRpm > max) max = r.drumRpm;
    });
    return max;
  }, [rows, extraColumns]);

  const rightAxisMax = useMemo(() => {
    const roundedUp = Math.ceil(maxRightAxisValue / 5) * 5;
    const padded = roundedUp + 5;
    return padded > 0 ? padded : 5;
  }, [maxRightAxisValue]);

  const rightTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let t = 0; t <= rightAxisMax; t += 5) {
      ticks.push(t);
    }
    return ticks;
  }, [rightAxisMax]);

  // Background Colors for Stages based on time intervals
  const phaseAreas = useMemo(() => {
    // Determine the end of the chart for open-ended phases
    let maxChartTime = 0;
    if (chartData.length > 0) {
      maxChartTime = chartData[chartData.length - 1].time;
    }

    if (!stages || stages.length === 0) {
      return [];
    }

    const timeToSeconds = (timeStr: string) => {
      const [mmRaw, ssRaw] = timeStr.split(':');
      const mm = Number((mmRaw ?? '').replace(/\D/g, ''));
      const ss = Number((ssRaw ?? '').replace(/\D/g, ''));
      return (Number.isFinite(mm) ? mm : 0) * 60 + (Number.isFinite(ss) ? ss : 0);
    };

    const secondsToTime = (totalSeconds: number) => {
      if (totalSeconds < 0) return '00:00';
      const m = Math.floor(totalSeconds / 60);
      const s = Math.floor(totalSeconds % 60);
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const calculateDuration = (startKey: string, endKey: string) => {
      const startStage = stages.find(s => s.key === startKey);
      const endStage = stages.find(s => s.key === endKey);

      if (!startStage || !endStage) return '00:00';

      const startSec = timeToSeconds(startStage.time);
      const endSec = timeToSeconds(endStage.time);

      return secondsToTime(endSec - startSec);
    };

    const calculatePhaseRatio = (phaseStartKey: string, phaseEndKey: string) => {
      const startStage = stages.find(s => s.key === 'start');
      const endStage = stages.find(s => s.key === 'end');
      const phaseStartStage = stages.find(s => s.key === phaseStartKey);
      const phaseEndStage = stages.find(s => s.key === phaseEndKey);

      if (!startStage || !endStage || !phaseStartStage || !phaseEndStage) return '0.00';

      const totalSec = timeToSeconds(endStage.time) - timeToSeconds(startStage.time);
      const phaseSec = timeToSeconds(phaseEndStage.time) - timeToSeconds(phaseStartStage.time);

      if (totalSec <= 0 || phaseSec <= 0) return '0.00';

      return ((phaseSec / totalSec) * 100).toFixed(2);
    };

    const dryingTime = calculateDuration('start', 'yellow');
    const maillardTime = calculateDuration('yellow', 'first_crack');
    const developmentTime = calculateDuration('first_crack', 'end');
    const dryingRatio = calculatePhaseRatio('start', 'yellow');
    const maillardRatio = calculatePhaseRatio('yellow', 'first_crack');
    const developmentRatio = calculatePhaseRatio('first_crack', 'end');

    const getStageTime = (key: string) => {
      const stage = stages.find((s) => s.key === key);
      return stage ? parseTime(stage.time) : null;
    };

    const startTime = getStageTime('start') ?? 0;
    const yellowTime = getStageTime('yellow');
    const firstCrackTime = getStageTime('first_crack');
    const endTime = getStageTime('end');

    // Fallback logic for open-ended phases
    // If a phase end is missing, extend to the next available phase start or chart end
    const effectiveYellow = yellowTime ?? (startTime !== null ? maxChartTime : null);
    const effectiveFirstCrack = firstCrackTime ?? (yellowTime !== null ? maxChartTime : null);
    const effectiveEnd = endTime ?? (firstCrackTime !== null ? maxChartTime : null);

    const areas: RoastPhaseArea[] = [];

    // Drying: Start -> Yellow (or End of Chart)
    if (effectiveYellow !== null && effectiveYellow > startTime) {
      areas.push({
        x1: startTime,
        x2: effectiveYellow,
        fill: '#fde047', // yellow-300
        opacity: 0.3,
        label: t('roast.profile.phases.drying'),
        detail: `${dryingTime} | ${dryingRatio}%`,
      });
    }

    // Maillard: Yellow -> First Crack (or End of Chart)
    if (yellowTime !== null && effectiveFirstCrack !== null && effectiveFirstCrack > yellowTime) {
      areas.push({
        x1: yellowTime,
        x2: effectiveFirstCrack,
        fill: '#f59e0b', // amber-500
        opacity: 0.3,
        label: t('roast.profile.phases.maillard'),
        detail: `${maillardTime} | ${maillardRatio}%`,
      });
    }

    // Development: First Crack -> End (or End of Chart)
    if (firstCrackTime !== null && effectiveEnd !== null && effectiveEnd > firstCrackTime) {
      areas.push({
        x1: firstCrackTime,
        x2: effectiveEnd,
        fill: '#b45309', // amber-700
        opacity: 0.3,
        label: t('roast.profile.phases.development'),
        detail: `${developmentTime} | ${developmentRatio}%`,
      });
    }

    return areas;
  }, [stages, chartData, t]);

  const stageMarkers = useMemo(() => {
    if (!stages || stages.length === 0) return [];

    return stages.map(s => {
      if (s.temp === '') return null;

      const labelTop = String(s.temp);
      let labelBottom = '';

      const timeStr = s.time;
      const timeSec = parseTime(timeStr);
      if (timeSec === null) return null;
      const timeInMinutes = timeSec;

      if (s.key === 'start') labelBottom = t('roast.profile.markers.charge');
      else if (s.key === 'turning_point') labelBottom = `${t('roast.profile.markers.tp')} ${timeStr}`;
      else if (s.key === 'yellow') labelBottom = `${t('roast.profile.stages.yellow')} ${timeStr}`;
      else if (s.key === 'first_crack') labelBottom = `${t('roast.profile.markers.fcs')} ${timeStr}`;
      else if (s.key === 'end') labelBottom = `${t('roast.profile.markers.drop')} ${timeStr}`;

      return {
        key: s.key,
        time: timeInMinutes,
        temp: Number(s.temp),
        labelTop,
        labelBottom,
      };
    }).filter(Boolean) as RoastStageMarker[];
  }, [stages, t]);

  return {
    chartData,
    xTicks,
    legendItems,
    tempAxisMax,
    tempTicks,
    rightAxisMax,
    rightTicks,
    phaseAreas,
    stageMarkers,
    hasAirTempData,
    hasDrumTempData,
    hasEnvironmentTempData,
    hasDrumRpmData,
  };
}
